"use strict";
/**
 * APITable <https://github.com/apitable/apitable>
 * Copyright (C) 2022 APITable Ltd. <https://apitable.com>
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.LastModifiedByField = void 0;
const joi_1 = __importDefault(require("joi"));
const lodash_1 = require("lodash");
const datasheet_1 = require("../../model/datasheet");
const utils_1 = require("../../model/utils");
const store_1 = require("../../exports/store");
const types_1 = require("../../types");
const member_base_field_1 = require("./member_base_field");
const validate_schema_1 = require("./validate_schema");
const i18n_1 = require("../../exports/i18n");
class LastModifiedByField extends member_base_field_1.MemberBaseField {
    constructor(field, state) {
        super(field, state);
        this.field = field;
        this.state = state;
    }
    get apiMetaProperty() {
        const userMap = store_1.Selectors.getUserMap(this.state);
        return (0, utils_1.getApiMetaUserProperty)(this.field.property.uuids, userMap);
    }
    get openValueJsonSchema() {
        return {
            type: 'object',
            title: this.field.name,
            properties: {
                id: {
                    type: 'string',
                    title: (0, i18n_1.t)(i18n_1.Strings.robot_variables_editor_ID),
                },
                name: {
                    type: 'string',
                    title: (0, i18n_1.t)(i18n_1.Strings.robot_variables_editor_name),
                },
                avatar: {
                    type: 'string',
                    title: (0, i18n_1.t)(i18n_1.Strings.robot_variables_editor_avatar),
                },
            }
        };
    }
    static defaultProperty() {
        return {
            uuids: [],
            datasheetId: '',
            collectType: types_1.CollectType.AllFields,
            fieldIdCollection: [],
        };
    }
    static createDefault(fieldMap) {
        return {
            id: datasheet_1.DatasheetActions.getNewFieldId(fieldMap),
            type: types_1.FieldType.LastModifiedBy,
            name: datasheet_1.DatasheetActions.getDefaultFieldName(fieldMap),
            property: this.defaultProperty(),
        };
    }
    get isComputed() {
        return true;
    }
    get basicValueType() {
        return types_1.BasicValueType.String;
    }
    recordEditable() {
        return false;
    }
    stdValueToCellValue() {
        return null;
    }
    validate(cv) {
        if (cv) {
            return false;
        }
        return true;
    }
    validateProperty() {
        return LastModifiedByField.propertySchema.validate(this.field.property);
    }
    validateCellValue() {
        return (0, validate_schema_1.joiErrorResult)("computed field shouldn't validate cellValue");
    }
    validateOpenWriteValue() {
        return (0, validate_schema_1.joiErrorResult)("computed field shouldn't validate cellValue");
    }
    getUuidByFieldUpdatedMap(fieldUpdatedMap, rangeIds) {
        const fieldUpdateInfo = rangeIds.reduce((prev, fieldId) => {
            const info = fieldUpdatedMap[fieldId];
            if (!(info === null || info === void 0 ? void 0 : info.at) || !(info === null || info === void 0 ? void 0 : info.by) || ((prev === null || prev === void 0 ? void 0 : prev.at) > (info === null || info === void 0 ? void 0 : info.at))) {
                return prev;
            }
            return { at: info.at, by: info.by };
        }, { at: 0, by: null });
        return (fieldUpdateInfo === null || fieldUpdateInfo === void 0 ? void 0 : fieldUpdateInfo.by) || null;
    }
    getCellValue(record) {
        var _a;
        const { collectType, fieldIdCollection } = this.field.property;
        const updatedMap = (_a = record.recordMeta) === null || _a === void 0 ? void 0 : _a.fieldUpdatedMap;
        // Depends on fieldUpdatedMap, otherwise returns null
        if (!updatedMap) {
            return null;
        }
        const isAllField = collectType === types_1.CollectType.AllFields;
        const fieldIds = isAllField ? Object.keys(updatedMap) : fieldIdCollection;
        const updatedBy = this.getUuidByFieldUpdatedMap(updatedMap, fieldIds);
        return updatedBy == null ? null : updatedBy;
    }
    // Get the uuid involved in all records
    getUuidsByRecordMap(recordMap) {
        const fieldUuids = [];
        const uuids = Object.values(recordMap)
            .reduce((acc, record) => {
            var _a, _b;
            const fieldUpdatedMap = (_a = record.recordMeta) === null || _a === void 0 ? void 0 : _a.fieldUpdatedMap;
            fieldUpdatedMap && Object.values(fieldUpdatedMap).forEach(updatedInfo => {
                (updatedInfo === null || updatedInfo === void 0 ? void 0 : updatedInfo.by) && fieldUuids.push(updatedInfo.by);
            });
            if ((_b = record.recordMeta) === null || _b === void 0 ? void 0 : _b.updatedBy) {
                acc.push(record.recordMeta.updatedBy);
            }
            return acc;
        }, []);
        return [...new Set([...uuids, ...fieldUuids])];
    }
    getUnitNames(cellValue) {
        const userMap = store_1.Selectors.getUserMap(this.state);
        if (!userMap)
            return null;
        return cellValue.map(uuid => {
            if (!userMap[uuid]) {
                return '';
            }
            return userMap[uuid].name;
        });
    }
    eq(cv1, cv2) {
        if (cv1 == null || cv2 == null)
            return cv1 === cv2;
        return (0, lodash_1.isEqual)(cv1, cv2);
    }
    getUnitIds(cellValue) {
        if (!cellValue) {
            return null;
        }
        return [cellValue].flat();
    }
    getUnits(cellValue) {
        const userMap = store_1.Selectors.getUserMap(this.state);
        if (!userMap)
            return null;
        return cellValue.reduce((ids, uuid) => {
            if (userMap[uuid]) {
                ids.push(userMap[uuid]);
            }
            return ids;
        }, []);
    }
    get openFieldProperty() {
        const { collectType, fieldIdCollection } = this.field.property;
        return {
            collectType,
            fieldIdCollection
        };
    }
    validateUpdateOpenProperty(updateProperty) {
        return LastModifiedByField.openUpdatePropertySchema.validate(updateProperty);
    }
    updateOpenFieldPropertyTransformProperty(openFieldProperty) {
        const { collectType, fieldIdCollection } = openFieldProperty;
        const { uuids, datasheetId } = this.field.property;
        const defaultProperty = LastModifiedByField.defaultProperty();
        return {
            uuids: uuids || [],
            datasheetId,
            collectType: collectType !== null && collectType !== void 0 ? collectType : defaultProperty.collectType,
            fieldIdCollection: fieldIdCollection || []
        };
    }
}
exports.LastModifiedByField = LastModifiedByField;
LastModifiedByField.propertySchema = joi_1.default.object({
    uuids: joi_1.default.array().items(joi_1.default.string()).required(),
    datasheetId: (0, validate_schema_1.datasheetIdString)().required(),
    // dependent field collection type
    collectType: joi_1.default.valid(types_1.CollectType.AllFields, types_1.CollectType.SpecifiedFields).required(),
    // dependent fields
    fieldIdCollection: joi_1.default.array().items(joi_1.default.string()).required(),
}).required();
LastModifiedByField.openUpdatePropertySchema = joi_1.default.object({
    collectType: joi_1.default.number().valid(types_1.CollectType.AllFields, types_1.CollectType.SpecifiedFields),
    fieldIdCollection: joi_1.default.array().items(joi_1.default.string())
}).required();
//# sourceMappingURL=last_modified_by_field.js.map